import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuComponentBase } from '../../../../../shared/base-classes/menu-component-base';
import { CdfiOutreachLogsService } from '../../services/cdfi-outreach-logs.service';
import { CDFIOutreachLog } from '../../models/cdfi-outreach-log';
import { takeUntil } from 'rxjs';
import { CdfiOutreachLogsTableComponent } from '../cdfi-outreach-logs-table/cdfi-outreach-logs-table.component';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { CdfiOutreachLogsDashboardComponent } from '../cdfi-outreach-logs-dashboard/cdfi-outreach-logs-dashboard.component';
import { PageSizeComponent } from '../../../../../shared/components/page-size/page-size.component';
import { FilterComponent } from '../../../../../shared/components/filter/filter.component';
import { FormsModule } from '@angular/forms';
import { OutreachFormType } from '../../../../outreach/models/outreach-form-data';
import { Router } from '@angular/router';
import { SearchInputComponent } from '../../../../../shared/components/search-input/search-input.component';

@Component({
  selector: 'app-cdfi-outreach-logs',
  standalone: true,
  imports: [
    CommonModule,
    CdfiOutreachLogsTableComponent,
    PageSizeComponent,
    PaginationComponent,
    CdfiOutreachLogsDashboardComponent,
    FilterComponent,
    FormsModule,
    SearchInputComponent,
  ],
  templateUrl: './cdfi-outreach-logs.component.html',
  styleUrl: './cdfi-outreach-logs.component.scss',
})
export class CdfiOutreachLogsComponent
  extends MenuComponentBase
  implements OnInit
{
  readonly DEEPROOTS_V2_SOURCE = 'wncsbi-deeproots-v2';

  currentPage = 1;
  pageSize = 50;
  loading = true;
  cdfiOutreachLogs: CDFIOutreachLog[] = [];
  totalCDFIOutreachLogs = 0;

  cdfiOutreachLogsService = inject(CdfiOutreachLogsService);
  router = inject(Router);
  searchQuery = '';
  filterBy = '';
  activeTab: 'WNCSBI_I' | 'WNCSBI_II' | 'DEEPROOTS_V2' = 'WNCSBI_I';
  showActiveClientsOnly = false;

  filterByOptions = [
    {
      option: 'All',
      value: '',
    },
    {
      option: 'Pending',
      value: '0',
    },
    {
      option: 'Sent',
      value: '1',
    },
    {
      option: 'Submitted',
      value: '2',
    },
    {
      option: 'Expired',
      value: '3',
    },
  ];

  tabs = [
    {
      id: 'WNCSBI_I' as const,
      label: 'WNCSBI I',
      outreachType: OutreachFormType.CRF,
    },
    {
      id: 'WNCSBI_II' as const,
      label: 'WNCSBI II',
      outreachType: OutreachFormType.GENERIC,
    },
    {
      id: 'DEEPROOTS_V2' as const,
      label: 'DeepRoots V2',
      outreachType: OutreachFormType.GENERIC,
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();

    // Check for tab query parameter
    const tabParam = this.route.snapshot.queryParams['tab'] as
      | 'WNCSBI_I'
      | 'WNCSBI_II'
      | 'DEEPROOTS_V2';
    if (
      tabParam &&
      (tabParam === 'WNCSBI_I' ||
        tabParam === 'WNCSBI_II' ||
        tabParam === 'DEEPROOTS_V2')
    ) {
      this.activeTab = tabParam;
    }

    this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
  }

  loadCDFIOutreachLogs(pageNumber: number, pageSize: number): void {
    const filterByValue = this.filterBy !== '' ? +this.filterBy : undefined;
    const searchParam = this.searchQuery || undefined;
    const currentTab = this.tabs.find((tab) => tab.id === this.activeTab);
    const outreachType = currentTab
      ? currentTab.outreachType
      : OutreachFormType.CRF;
    const source = this.getCurrentSource();
    const leadStatus = this.showActiveClientsOnly ? 7 : undefined;

    this.cdfiOutreachLogsService
      .getCdfiOutreachLogs(
        pageNumber,
        pageSize,
        outreachType,
        filterByValue,
        searchParam,
        source,
        leadStatus,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.cdfiOutreachLogs = response.results;
          this.totalCDFIOutreachLogs = response.count;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  onFilterByChange(value: string): void {
    this.filterBy = value;
    this.currentPage = 1;
    this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
  }

  onSearchQueryChange(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
    }
  }

  onTabChange(tabId: 'WNCSBI_I' | 'WNCSBI_II' | 'DEEPROOTS_V2'): void {
    if (this.activeTab !== tabId) {
      this.activeTab = tabId;
      this.currentPage = 1;
      this.searchQuery = '';
      this.filterBy = '';
      this.showActiveClientsOnly = false;
      this.loading = true;

      // Update query parameter
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabId },
        queryParamsHandling: 'merge',
      });

      this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
    }
  }

  getCurrentOutreachType(): OutreachFormType {
    const currentTab = this.tabs.find((tab) => tab.id === this.activeTab);
    return currentTab ? currentTab.outreachType : OutreachFormType.CRF;
  }

  getCurrentSource(): string | undefined {
    if (this.activeTab === 'WNCSBI_II') {
      return 'wncsbi2';
    }

    if (this.activeTab === 'DEEPROOTS_V2') {
      return this.DEEPROOTS_V2_SOURCE;
    }

    return undefined;
  }

  onActiveClientsToggle(): void {
    this.currentPage = 1;

    if (this.showActiveClientsOnly) {
      // When enabled, automatically set filter to Submitted (value '2')
      this.filterBy = '2';
    } else {
      // When disabled, revert filter to All (empty string)
      this.filterBy = '';
    }

    this.loadCDFIOutreachLogs(this.currentPage, this.pageSize);
  }
}
